import {
  Close,
  Search,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { EmployeePersona } from "../../../types";
import { hostname, addImageDimensions } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

interface EmployeeTemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectPersona: (persona: EmployeePersona) => void;
  getToken: () => Promise<string>;
}

const EmployeeTemplatesDialog: React.FC<EmployeeTemplatesDialogProps> = ({
  open,
  onClose,
  onSelectPersona,
  getToken,
}) => {
  const { employee: employeeText } = useEmployeeTerminology();
  const [personas, setPersonas] = useState<EmployeePersona[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      fetchPersonas();
      fetchCategories();
    }
  }, [open]);

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (selectedCategory) {
        params.append("category", selectedCategory);
      }
      const response = await axios.get(
        `${hostname}/employee-personas${params.toString() ? `?${params}` : ""}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPersonas(response.data.personas || []);
    } catch (error) {
      console.error("Error fetching personas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/employee-personas/categories`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPersonas();
    }
  }, [selectedCategory]);

  const filteredPersonas = personas.filter((persona) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      persona.title.toLowerCase().includes(query) ||
      persona.name?.toLowerCase().includes(query) ||
      persona.description?.toLowerCase().includes(query) ||
      persona.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  const handleClose = () => {
    setSearchQuery("");
    setSelectedCategory(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "80vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Pre-Built {employeeText}s</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          {/* Search and Filter */}
          <Stack spacing={2} sx={{ flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder="Search pre-built..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="All"
                variant={selectedCategory === null ? "filled" : "outlined"}
                color={selectedCategory === null ? "primary" : "default"}
                onClick={() => setSelectedCategory(null)}
                size="small"
              />
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  variant={selectedCategory === category ? "filled" : "outlined"}
                  color={selectedCategory === category ? "primary" : "default"}
                  onClick={() => setSelectedCategory(category)}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>

          {/* Persona Grid */}
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, pt: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredPersonas.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery
                    ? "No results match your search"
                    : "No pre-built options available yet"}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredPersonas.map((persona) => (
                <Grid item xs={12} sm={6} md={4} key={persona.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => {
                        handleClose();
                        onSelectPersona(persona);
                      }}
                      sx={{ height: "100%", p: 2 }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Avatar
                          src={persona.picture ? addImageDimensions(persona.picture, 200, 200) : undefined}
                          sx={{ width: 80, height: 80, flexShrink: 0 }}
                        >
                          {persona.title?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            noWrap
                          >
                            {persona.title}
                          </Typography>
                          {persona.name && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              display="block"
                            >
                              {persona.name}
                            </Typography>
                          )}
                          {persona.category && (
                            <Chip
                              label={persona.category}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          {persona.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {persona.description}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeTemplatesDialog;
